/**
 * AdminProjects — /admin/projects.
 *
 * Admin-only project management:
 *   - Full Studio styling pass (rounded-2xl, bg-cloud/60, paper-light
 *     inputs, pine accents)
 *   - Columns: Name (+ alias pills) · Last activity · Drafts ·
 *     Finalized · Owners (avatar stack) · Status · Actions
 *   - Status filter chips: Active / Archived / Orphans / Trash-only
 *     (multi-toggle, all-on by default shows everything)
 *   - Sortable column headers (Name, Last activity, Drafts, Finalized).
 *     Default sort = most recent activity first
 *   - Alias pills per-row with × to drop + "+ Add" inline input
 *   - Click row → side drawer showing meetings scoped to this project
 *   - Per-row Audit modal (patch / merge history, resolved admin
 *     display names, humanized summaries)
 *   - Existing actions preserved: inline rename, merge, archive,
 *     "clean up empty projects" bulk action
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  ChevronRight,
  GitMerge,
  History,
  Pencil,
  Plus,
  Search,
  X,
  Check,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/apiClient.ts';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';
import { Modal } from '../../components/ui/Modal.tsx';
import { AttendeeAvatar } from '../../components/editor/AttendeeAvatar.tsx';

interface ProjectOwner {
  userId: string;
  displayName: string;
  email: string;
  headshotUrl: string | null;
  meetingCount: number;
}

interface AdminProject {
  id: string;
  name: string;
  aliases: string[];
  usageCount: number;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  lastActivityAt: string | null;
  draftCount: number;
  finalizedCount: number;
  softDeletedCount: number;
  topOwners: ProjectOwner[];
  totalOwnerCount: number;
}

interface CleanupResult {
  projectsDeleted: number;
  meetingsDeleted: number;
  breakdown: {
    zeroMeetingProjects: number;
    softDeletedOnlyProjects: number;
    softDeletedMeetingsPurged: number;
  };
}

interface MergeResult {
  reparentedMeetings: number;
  target: {
    id: string;
    name: string;
    aliases: string[];
    usageCount: number;
  };
}

type StatusFilter = 'active' | 'archived' | 'orphans' | 'trash-only';
type SortKey = 'name' | 'lastActivityAt' | 'draftCount' | 'finalizedCount';
type SortDir = 'asc' | 'desc';

export function AdminProjects() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('');
  const [confirmCleanupOpen, setConfirmCleanupOpen] = useState(false);
  const [lastCleanup, setLastCleanup] = useState<CleanupResult | null>(null);
  const [mergeSource, setMergeSource] = useState<AdminProject | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [rowError, setRowError] = useState<{ id: string; msg: string } | null>(
    null,
  );
  const [drawerProject, setDrawerProject] = useState<AdminProject | null>(null);
  const [auditProject, setAuditProject] = useState<AdminProject | null>(null);

  // Status chip set — active by default shows every non-archived
  // project. Toggling chips narrows / widens the visible set.
  const [activeFilters, setActiveFilters] = useState<Set<StatusFilter>>(
    new Set<StatusFilter>(['active', 'archived']),
  );

  const [sortKey, setSortKey] = useState<SortKey>('lastActivityAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const listQuery = useQuery({
    queryKey: ['admin', 'projects'],
    queryFn: () => api.get<{ projects: AdminProject[] }>('/admin/projects'),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'projects'] });
    qc.invalidateQueries({ queryKey: ['projects'] });
  };

  const cleanup = useMutation({
    mutationFn: () => api.post<CleanupResult>('/admin/projects/cleanup-empty'),
    onSuccess: (result) => {
      setLastCleanup(result);
      setConfirmCleanupOpen(false);
      invalidateAll();
    },
  });

  const patchProject = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { name?: string; archived?: boolean; aliases?: string[] };
    }) => api.patch<{ project: AdminProject }>(`/admin/projects/${id}`, body),
    onSuccess: () => {
      setEditingId(null);
      setEditingName('');
      setRowError(null);
      invalidateAll();
    },
    onError: (err, vars) => {
      setRowError({
        id: vars.id,
        msg: err instanceof ApiError ? err.message : 'Update failed',
      });
    },
  });

  const mergeMut = useMutation({
    mutationFn: ({
      sourceId,
      targetId,
    }: {
      sourceId: string;
      targetId: string;
    }) =>
      api.post<MergeResult>(`/admin/projects/${sourceId}/merge`, {
        targetProjectId: targetId,
      }),
    onSuccess: () => {
      setMergeSource(null);
      setMergeTargetId('');
      invalidateAll();
    },
  });

  const projects = listQuery.data?.projects ?? [];

  // Client-side search: matches name OR any alias, case-insensitive.
  const searchFiltered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.aliases.some((a) => a.toLowerCase().includes(q)),
    );
  }, [projects, filter]);

  // Status chip filter — each chip gates a predicate. A project is
  // visible if it matches AT LEAST ONE of the active chips.
  const visible = useMemo(() => {
    if (activeFilters.size === 0) return [];
    return searchFiltered.filter((p) => {
      const isOrphan = p.draftCount === 0 && p.finalizedCount === 0;
      const trashOnly = isOrphan && p.softDeletedCount > 0;
      const trulyEmpty = isOrphan && p.softDeletedCount === 0;
      if (activeFilters.has('active') && !p.archived && !isOrphan) return true;
      if (activeFilters.has('archived') && p.archived) return true;
      if (activeFilters.has('orphans') && trulyEmpty && !p.archived) return true;
      if (activeFilters.has('trash-only') && trashOnly && !p.archived) return true;
      return false;
    });
  }, [searchFiltered, activeFilters]);

  // Sort.
  const sorted = useMemo(() => {
    const out = [...visible];
    const dir = sortDir === 'asc' ? 1 : -1;
    out.sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return a.name.localeCompare(b.name) * dir;
        case 'lastActivityAt': {
          const at = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
          const bt = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
          return (at - bt) * dir;
        }
        case 'draftCount':
          return (a.draftCount - b.draftCount) * dir;
        case 'finalizedCount':
          return (a.finalizedCount - b.finalizedCount) * dir;
      }
    });
    return out;
  }, [visible, sortKey, sortDir]);

  const orphanCount = projects.filter(
    (p) => p.draftCount === 0 && p.finalizedCount === 0,
  ).length;

  const onSort = (k: SortKey) => {
    if (k === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(k);
      setSortDir(k === 'name' ? 'asc' : 'desc');
    }
  };
  const toggleFilter = (k: StatusFilter) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const startRename = (p: AdminProject) => {
    setEditingId(p.id);
    setEditingName(p.name);
    setRowError(null);
  };
  const cancelRename = () => {
    setEditingId(null);
    setEditingName('');
    setRowError(null);
  };
  const commitRename = (p: AdminProject) => {
    const next = editingName.trim();
    if (!next || next === p.name) {
      cancelRename();
      return;
    }
    patchProject.mutate({ id: p.id, body: { name: next } });
  };
  const toggleArchive = (p: AdminProject) => {
    patchProject.mutate({ id: p.id, body: { archived: !p.archived } });
  };
  const removeAlias = (p: AdminProject, alias: string) => {
    patchProject.mutate({
      id: p.id,
      body: { aliases: p.aliases.filter((a) => a !== alias) },
    });
  };
  const addAlias = (p: AdminProject, alias: string) => {
    const t = alias.trim();
    if (!t) return;
    if (p.aliases.some((a) => a.toLowerCase() === t.toLowerCase())) return;
    patchProject.mutate({
      id: p.id,
      body: { aliases: [...p.aliases, t] },
    });
  };

  return (
    <div>
      {/* Mockup .projects-head: 18px charcoal/600 title + 13px
          charcoal/65 subtitle + .admin-btn primary CTA on the right. */}
      <div className="mb-4 flex items-start justify-between gap-6">
        <div>
          <h2 className="text-[18px] font-semibold text-charcoal">Projects</h2>
          <p className="mt-1 max-w-[60ch] text-[13px] text-charcoal/65">
            Every project in the system with activity, owners, rename,
            archive, merge, and audit.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setConfirmCleanupOpen(true)}
          disabled={orphanCount === 0 || cleanup.isPending}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-pine bg-pine px-4 py-2 text-[13px] font-medium text-cream transition-all hover:-translate-y-px hover:bg-pine-deep disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
          title={
            orphanCount === 0
              ? 'No orphan projects to clean up.'
              : `Delete ${orphanCount} orphan project${orphanCount === 1 ? '' : 's'}`
          }
        >
          Clean up empty projects
          {orphanCount > 0 && ` (${orphanCount})`}
        </button>
      </div>

      {/* ── Status filter chips ─────────────────────────────────── */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FilterChip
          active={activeFilters.has('active')}
          onClick={() => toggleFilter('active')}
        >
          Active
        </FilterChip>
        <FilterChip
          active={activeFilters.has('archived')}
          onClick={() => toggleFilter('archived')}
        >
          Archived
        </FilterChip>
        <FilterChip
          active={activeFilters.has('orphans')}
          onClick={() => toggleFilter('orphans')}
          tone="amber"
        >
          Orphans
        </FilterChip>
        <FilterChip
          active={activeFilters.has('trash-only')}
          onClick={() => toggleFilter('trash-only')}
          tone="amber"
        >
          Trash-only
        </FilterChip>
        <span className="ml-auto text-[11px] text-charcoal/55">
          {sorted.length} of {projects.length} project
          {projects.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* Mockup .search-pill: rounded-[14px] cloud/60 + charcoal/15
          + py-3 pl-11 with positioned icon + pine focus ring. */}
      <div className="mb-3.5 relative">
        <Search
          size={16}
          aria-hidden
          className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/45"
        />
        <input
          {...NO_AUTOFILL}
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search by project name or alias…"
          className="w-full rounded-[14px] border border-charcoal/15 bg-cloud/60 py-3 pl-11 pr-4 text-[13px] text-charcoal placeholder:text-charcoal/45 focus:border-pine focus:bg-bone focus:outline-none focus:ring-[3px] focus:ring-pine/15"
        />
      </div>

      {/* Banners — terra-cotta for blocking errors, pine-soft for
          success readouts. Matches the rest of the app's banner family. */}
      {cleanup.isError && (
        <div className="mb-3.5 rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-4 py-3 text-[13px] text-terra-cotta">
          Cleanup failed:{' '}
          {cleanup.error instanceof ApiError
            ? cleanup.error.message
            : 'Unknown error'}
        </div>
      )}
      {lastCleanup && !cleanup.isError && (
        <div className="mb-3.5 rounded-2xl border border-pine/25 bg-pine/[0.06] px-4 py-3 text-[13px] text-pine">
          Cleanup complete - removed {lastCleanup.projectsDeleted} project
          {lastCleanup.projectsDeleted === 1 ? '' : 's'} and{' '}
          {lastCleanup.meetingsDeleted} soft-deleted meeting
          {lastCleanup.meetingsDeleted === 1 ? '' : 's'}.
          <span className="ml-1 text-charcoal/70">
            ({lastCleanup.breakdown.zeroMeetingProjects} with no meetings,{' '}
            {lastCleanup.breakdown.softDeletedOnlyProjects} with trash only)
          </span>
        </div>
      )}
      {mergeMut.isError && (
        <div className="mb-3.5 rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-4 py-3 text-[13px] text-terra-cotta">
          Merge failed:{' '}
          {mergeMut.error instanceof ApiError
            ? mergeMut.error.message
            : 'Unknown error'}
        </div>
      )}

      {/* Loading / empty — italic charcoal/55 to match the rest of
          the admin states. */}
      {listQuery.isLoading && (
        <div className="py-10 text-center text-[13px] italic text-charcoal/55">
          Loading projects…
        </div>
      )}
      {listQuery.isError && (
        <div className="rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-4 py-3 text-[13px] text-terra-cotta">
          Failed to load projects.
        </div>
      )}
      {!listQuery.isLoading && !listQuery.isError && sorted.length === 0 && (
        <div className="rounded-[14px] border border-dashed border-charcoal/20 bg-cloud/40 px-4 py-10 text-center text-[12px] italic text-charcoal/55">
          {filter
            ? `No projects match "${filter}".`
            : activeFilters.size === 0
              ? 'Enable at least one status chip above to see projects.'
              : 'No projects match these filters.'}
        </div>
      )}

      {/* Mockup .admin-table family — bone outer + cloud thead +
          10px tracked-uppercase headers. Row tinting: orphans get a
          warm amber wash (mockup row style: rgba(245, 224, 158, 0.18)). */}
      {sorted.length > 0 && (
        <div className="overflow-x-auto rounded-[14px] border border-charcoal/8 bg-bone shadow-[0_1px_2px_rgba(43,42,47,0.04)]">
          <table className="w-full text-[13px]">
            <thead className="border-b border-charcoal/10 bg-cloud/60 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-charcoal/65">
              <tr>
                <Th
                  sortKey="name"
                  current={sortKey}
                  dir={sortDir}
                  onSort={onSort}
                >
                  Name
                </Th>
                <Th
                  sortKey="lastActivityAt"
                  current={sortKey}
                  dir={sortDir}
                  onSort={onSort}
                >
                  Last activity
                </Th>
                <Th
                  sortKey="draftCount"
                  current={sortKey}
                  dir={sortDir}
                  onSort={onSort}
                  align="right"
                >
                  Drafts
                </Th>
                <Th
                  sortKey="finalizedCount"
                  current={sortKey}
                  dir={sortDir}
                  onSort={onSort}
                  align="right"
                >
                  Finalized
                </Th>
                <th className="px-3.5 py-2.5">Owners</th>
                <th className="px-3.5 py-2.5">Status</th>
                <th className="px-3.5 py-2.5 text-right" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => {
                const isOrphan = p.draftCount === 0 && p.finalizedCount === 0;
                const trashOnly = isOrphan && p.softDeletedCount > 0;
                const trulyEmpty = isOrphan && p.softDeletedCount === 0;
                const isEditing = editingId === p.id;
                const rowErr = rowError?.id === p.id ? rowError.msg : null;
                return (
                  <tr
                    key={p.id}
                    className={
                      'border-b border-charcoal/[0.06] last:border-b-0 transition-colors hover:bg-pine/[0.04] ' +
                      (isOrphan ? 'bg-amber-100/30 hover:bg-amber-100/40' : '')
                    }
                  >
                    {/* Name + alias pills */}
                    <td className="px-3.5 py-3">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <input
                            {...NO_AUTOFILL}
                            autoFocus
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitRename(p);
                              if (e.key === 'Escape') cancelRename();
                            }}
                            disabled={patchProject.isPending}
                            className="w-full rounded-[8px] border border-charcoal/15 bg-cream px-2.5 py-1 text-[13px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
                          />
                          <button
                            type="button"
                            onClick={() => commitRename(p)}
                            disabled={patchProject.isPending}
                            className="rounded-full bg-pine p-1.5 text-cloud transition-colors hover:bg-pine/90 disabled:opacity-50"
                            title="Save (Enter)"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={cancelRename}
                            disabled={patchProject.isPending}
                            className="rounded-full border border-charcoal/25 p-1.5 text-charcoal transition-colors hover:border-pine hover:text-pine disabled:opacity-50"
                            title="Cancel (Esc)"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <button
                            type="button"
                            onClick={() => setDrawerProject(p)}
                            className="group/name flex items-center gap-1 text-left font-medium text-charcoal hover:text-pine"
                          >
                            <span>{p.name}</span>
                            <ChevronRight
                              size={12}
                              className="text-charcoal/30 transition-colors group-hover/name:text-pine"
                            />
                          </button>
                          {/* Alias pills — removable + add input */}
                          <AliasPills
                            aliases={p.aliases}
                            onRemove={(a) => removeAlias(p, a)}
                            onAdd={(a) => addAlias(p, a)}
                            disabled={patchProject.isPending}
                          />
                          {rowErr && (
                            <div className="mt-1 text-[11px] text-red-600">
                              {rowErr}
                            </div>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Last activity */}
                    <td
                      className="px-3 py-2.5 text-[12px] tabular-nums text-charcoal"
                      title={
                        p.lastActivityAt
                          ? new Date(p.lastActivityAt).toLocaleString()
                          : ''
                      }
                    >
                      {p.lastActivityAt ? relativeTime(p.lastActivityAt) : '-'}
                    </td>

                    {/* Counts */}
                    <td className="px-3 py-2.5 text-right tabular-nums text-charcoal">
                      {p.draftCount}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-charcoal">
                      {p.finalizedCount}
                    </td>

                    {/* Owner avatar stack */}
                    <td className="px-3.5 py-3">
                      <OwnerStack
                        owners={p.topOwners}
                        total={p.totalOwnerCount}
                      />
                    </td>

                    {/* Status */}
                    <td className="px-3.5 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        {trulyEmpty && (
                          <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-700">
                            No meetings
                          </span>
                        )}
                        {trashOnly && (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                            Trash only
                          </span>
                        )}
                        {p.archived && (
                          <span className="rounded-full bg-charcoal/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-charcoal/70">
                            Archived
                          </span>
                        )}
                        {p.softDeletedCount > 0 && !trashOnly && (
                          <span className="text-[10px] text-charcoal/50">
                            +{p.softDeletedCount} trashed
                          </span>
                        )}
                        {!isOrphan && !p.archived && p.softDeletedCount === 0 && (
                          <span className="text-[11px] text-charcoal/40">
                            -
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-3.5 py-3">
                      {!isEditing && (
                        <div className="flex items-center justify-end gap-1">
                          <IconBtn
                            label="Rename"
                            onClick={() => startRename(p)}
                          >
                            <Pencil size={14} />
                          </IconBtn>
                          <IconBtn
                            label="Merge into another project"
                            onClick={() => {
                              setMergeSource(p);
                              setMergeTargetId('');
                            }}
                          >
                            <GitMerge size={14} />
                          </IconBtn>
                          <IconBtn
                            label={p.archived ? 'Unarchive' : 'Archive'}
                            onClick={() => toggleArchive(p)}
                            disabled={patchProject.isPending}
                          >
                            {p.archived ? (
                              <ArchiveRestore size={14} />
                            ) : (
                              <Archive size={14} />
                            )}
                          </IconBtn>
                          <IconBtn
                            label="View audit log"
                            onClick={() => setAuditProject(p)}
                          >
                            <History size={14} />
                          </IconBtn>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Cleanup confirm dialog ──────────────────────────────── */}
      <Modal
        isOpen={confirmCleanupOpen}
        onClose={() => setConfirmCleanupOpen(false)}
        size="md"
        ariaLabel="Confirm orphan project cleanup"
      >
        <div className="p-6">
          <h3 className="text-lg font-semibold text-charcoal">
            Delete {orphanCount} orphan project{orphanCount === 1 ? '' : 's'}?
          </h3>
          <p className="mt-2 text-sm text-charcoal/70">
            This removes projects with no active meetings. Projects whose
            only meetings are in the trash will have those trashed meetings
            permanently deleted too. Cost accounting + error history are
            preserved. This cannot be undone.
          </p>
          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmCleanupOpen(false)}
              disabled={cleanup.isPending}
              className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => cleanup.mutate()}
              disabled={cleanup.isPending}
              className="rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90 disabled:opacity-50"
            >
              {cleanup.isPending ? 'Cleaning up…' : 'Delete orphans'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Merge dialog ────────────────────────────────────────── */}
      <Modal
        isOpen={!!mergeSource}
        onClose={() => {
          setMergeSource(null);
          setMergeTargetId('');
        }}
        size="md"
        ariaLabel="Merge project"
      >
        {mergeSource && (
          <div className="p-6">
            <h3 className="text-lg font-semibold text-charcoal">
              Merge &ldquo;{mergeSource.name}&rdquo; into…
            </h3>
            <p className="mt-2 text-sm text-charcoal/70">
              All meetings move to the target project.{' '}
              <span className="font-medium">&ldquo;{mergeSource.name}&rdquo;</span>{' '}
              is added as an alias of the target (so the ProjectPicker still
              finds it by the old name), usage counts are summed, then the
              source project is deleted. This cannot be undone.
            </p>
            <label className="mt-5 block text-xs font-semibold uppercase tracking-[0.14em] text-pine">
              Target project
            </label>
            <select
              value={mergeTargetId}
              onChange={(e) => setMergeTargetId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-charcoal/20 bg-paper-light px-3 py-2 text-sm text-charcoal focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
            >
              <option value="">- Pick a target -</option>
              {projects
                .filter((p) => p.id !== mergeSource.id)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.archived ? ' (archived)' : ''}
                    {` - ${p.draftCount + p.finalizedCount} active`}
                  </option>
                ))}
            </select>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setMergeSource(null);
                  setMergeTargetId('');
                }}
                disabled={mergeMut.isPending}
                className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  mergeMut.mutate({
                    sourceId: mergeSource.id,
                    targetId: mergeTargetId,
                  })
                }
                disabled={!mergeTargetId || mergeMut.isPending}
                className="rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90 disabled:opacity-50"
              >
                {mergeMut.isPending ? 'Merging…' : 'Merge'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Side drawer: project detail + meeting list ───────── */}
      {drawerProject && (
        <ProjectDrawer
          project={drawerProject}
          onClose={() => setDrawerProject(null)}
        />
      )}

      {/* ── Audit modal ──────────────────────────────────────── */}
      {auditProject && (
        <ProjectAuditModal
          project={auditProject}
          onClose={() => setAuditProject(null)}
        />
      )}
    </div>
  );
}

/* ─── Building blocks ─────────────────────────────────────────── */

function FilterChip({
  active,
  onClick,
  tone = 'default',
  children,
}: {
  active: boolean;
  onClick: () => void;
  tone?: 'default' | 'amber';
  children: ReactNode;
}) {
  // Mockup .filter-chip family — cloud/60 idle + pine fill on
  // active. Amber tone (orphans/trash-only) uses #E5A62E per
  // .filter-chip--active-amber spec.
  const activeCls =
    tone === 'amber'
      ? 'border-amber-500 bg-amber-500 text-white'
      : 'border-pine bg-pine text-cream';
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ' +
        (active
          ? activeCls
          : 'border-charcoal/20 bg-cloud/60 text-charcoal hover:border-pine hover:text-pine')
      }
    >
      {children}
    </button>
  );
}

function Th({
  sortKey: key,
  current,
  dir,
  onSort,
  align = 'left',
  children,
}: {
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  align?: 'left' | 'right';
  children: ReactNode;
}) {
  // Same .admin-table thead th spec as AdminUsers / AdminMeetings.
  const isActive = current === key;
  return (
    <th
      className={
        'cursor-pointer select-none px-3.5 py-2.5 transition-colors hover:text-pine ' +
        (align === 'right' ? 'text-right ' : '') +
        (isActive ? 'text-pine' : '')
      }
      onClick={() => onSort(key)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {isActive && (
          <span className="text-[9px] leading-none">
            {dir === 'asc' ? '▲' : '▼'}
          </span>
        )}
      </span>
    </th>
  );
}

function IconBtn({
  children,
  label,
  onClick,
  disabled,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  // Mockup .icon-btn-sm: 28x28 cream + charcoal/15 + 12px icon,
  // hover pine border + pine-soft bg + pine icon.
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-charcoal/15 bg-cream text-charcoal/60 transition-all hover:border-pine hover:bg-pine/[0.08] hover:text-pine disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}

function AliasPills({
  aliases,
  onRemove,
  onAdd,
  disabled,
}: {
  aliases: string[];
  onRemove: (alias: string) => void;
  onAdd: (alias: string) => void;
  disabled: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const commit = () => {
    const t = draft.trim();
    if (t) onAdd(t);
    setDraft('');
    setAdding(false);
  };
  if (aliases.length === 0 && !adding) {
    return (
      <button
        type="button"
        onClick={() => setAdding(true)}
        disabled={disabled}
        className="mt-1 inline-flex items-center gap-1 text-[10px] text-charcoal/40 transition-colors hover:text-pine disabled:opacity-50"
      >
        <Plus size={10} /> add alias
      </button>
    );
  }
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {/* Mockup .alias-pill: cloud/60 + charcoal/15 + 10px charcoal/70.
          Remove ✕ hovers terra-cotta. */}
      {aliases.map((a) => (
        <span
          key={a}
          className="group/alias inline-flex items-center gap-1 rounded-full border border-charcoal/15 bg-cloud/60 px-2 py-0.5 text-[10px] text-charcoal/70"
        >
          {a}
          <button
            type="button"
            onClick={() => onRemove(a)}
            disabled={disabled}
            className="text-charcoal/35 transition-colors hover:text-terra-cotta disabled:opacity-50"
            title={`Remove alias "${a}"`}
            aria-label={`Remove alias ${a}`}
          >
            <X size={10} />
          </button>
        </span>
      ))}
      {adding ? (
        <input
          {...NO_AUTOFILL}
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
              setDraft('');
              setAdding(false);
            }
          }}
          onBlur={commit}
          placeholder="new alias"
          className="rounded-full border border-pine/40 bg-cream px-2 py-0.5 text-[10px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[2px] focus:ring-pine/15"
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          disabled={disabled}
          className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-charcoal/25 bg-transparent px-1.5 py-0.5 text-[10px] text-charcoal/40 transition-colors hover:border-pine hover:text-pine disabled:opacity-50"
          title="Add alias"
        >
          <Plus size={10} />
        </button>
      )}
    </div>
  );
}

function OwnerStack({
  owners,
  total,
}: {
  owners: ProjectOwner[];
  total: number;
}) {
  if (owners.length === 0) {
    return <span className="text-[11px] text-charcoal/35">-</span>;
  }
  const overflow = Math.max(0, total - owners.length);
  return (
    <div className="flex items-center -space-x-1.5">
      {owners.map((o) => (
        <span
          key={o.userId}
          title={`${o.displayName} - ${o.meetingCount} meeting${o.meetingCount === 1 ? '' : 's'}`}
          className="relative ring-2 ring-bone"
        >
          <AttendeeAvatar
            name={o.displayName}
            headshotUrl={o.headshotUrl}
            size={22}
          />
        </span>
      ))}
      {overflow > 0 && (
        <span
          title={`${overflow} more owner${overflow === 1 ? '' : 's'}`}
          className="relative flex h-[22px] w-[22px] items-center justify-center rounded-full bg-charcoal/10 text-[9px] font-semibold text-charcoal/60 ring-2 ring-bone"
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}

/** "1 hour ago", "3 days ago", "Apr 12". Keeps the absolute date for
 *  anything older than 14 days so the column stays scannable. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.floor(diff / (1000 * 60));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 14) return `${days}d ago`;
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/* ─── Side drawer: project detail + recent meetings ─────────────── */

interface DrawerMeeting {
  id: string;
  title: string;
  meetingDate: string;
  status: 'draft' | 'finalized' | 'soft_deleted';
  ownerName: string | null;
  updatedAt: string;
}

function ProjectDrawer({
  project,
  onClose,
}: {
  project: AdminProject;
  onClose: () => void;
}) {
  const meetingsQ = useQuery({
    queryKey: ['admin', 'meetings', 'by-project', project.id],
    queryFn: () =>
      api.get<{ meetings: DrawerMeeting[] }>('/admin/meetings', {
        projectId: project.id,
        limit: 10,
      }),
  });
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onMouseDown={onClose}
    >
      {/* Mockup ProjectDrawer: bone surface + charcoal/12 border +
          sticky bone header with cream-tinted alias pills. */}
      <aside
        className="h-full w-full max-w-md overflow-y-auto border-l border-charcoal/12 bg-bone shadow-[-4px_0_24px_rgba(43,42,47,0.12)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 border-b border-charcoal/10 bg-bone/95 px-5 py-4 backdrop-blur">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="truncate text-[16px] font-semibold text-charcoal">
                {project.name}
              </h2>
              {project.aliases.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {project.aliases.map((a) => (
                    <span
                      key={a}
                      className="rounded-full border border-charcoal/15 bg-cream px-2 py-0.5 text-[10px] text-charcoal/70"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1 text-charcoal/50 transition-colors hover:bg-charcoal/10 hover:text-charcoal"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="space-y-5 px-5 py-5 text-sm">
          {/* Stats grid */}
          <div className="grid grid-cols-4 gap-3">
            <StatCell label="Drafts" value={project.draftCount} />
            <StatCell
              label="Finalized"
              value={project.finalizedCount}
              accent="pine"
            />
            <StatCell
              label="Trashed"
              value={project.softDeletedCount}
              accent={project.softDeletedCount > 0 ? 'amber' : 'default'}
            />
            <StatCell
              label="Owners"
              value={project.totalOwnerCount}
            />
          </div>

          <div className="text-[11px] text-charcoal/55">
            Last activity:{' '}
            <b className="text-charcoal">
              {project.lastActivityAt
                ? new Date(project.lastActivityAt).toLocaleString()
                : '-'}
            </b>
          </div>

          {/* Recent meetings */}
          <div>
            <div className="mb-2 flex items-baseline justify-between">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-pine">
                Recent meetings
              </h3>
              <Link
                to={`/admin/meetings?projectId=${project.id}`}
                className="text-[11px] text-charcoal/60 hover:text-pine hover:underline"
              >
                View all →
              </Link>
            </div>
            {meetingsQ.isLoading && (
              <div className="py-4 text-center text-xs italic text-charcoal/50">
                Loading…
              </div>
            )}
            {meetingsQ.data && meetingsQ.data.meetings.length === 0 && (
              <div className="py-4 text-center text-xs italic text-charcoal/50">
                No meetings yet.
              </div>
            )}
            {meetingsQ.data && meetingsQ.data.meetings.length > 0 && (
              <ul className="divide-y divide-charcoal/10">
                {meetingsQ.data.meetings.map((m) => (
                  <li key={m.id} className="py-2">
                    <Link
                      to={`/meetings/${m.id}/edit`}
                      className="flex items-baseline justify-between gap-2"
                    >
                      <span
                        className={
                          'truncate font-medium ' +
                          (m.status === 'soft_deleted'
                            ? 'text-charcoal/50 line-through'
                            : 'text-charcoal hover:text-pine')
                        }
                      >
                        {m.title || (
                          <span className="italic text-charcoal/45">
                            Untitled
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-[10px] tabular-nums text-charcoal/50">
                        {m.meetingDate}
                      </span>
                    </Link>
                    <div className="text-[10px] text-charcoal/40">
                      {m.ownerName ?? '-'}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="text-[10px] text-charcoal/40">
            Project created {new Date(project.createdAt).toLocaleDateString()}
          </div>
        </div>
      </aside>
    </div>
  );
}

function StatCell({
  label,
  value,
  accent = 'default',
}: {
  label: string;
  value: number;
  accent?: 'default' | 'pine' | 'amber';
}) {
  const color =
    accent === 'pine'
      ? 'text-pine'
      : accent === 'amber'
        ? 'text-amber-700'
        : 'text-charcoal';
  return (
    <div className="rounded-xl border border-charcoal/10 bg-paper-light px-2 py-2 text-center">
      <div className={`text-lg font-semibold ${color}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-[0.12em] text-charcoal/55">
        {label}
      </div>
    </div>
  );
}

/* ─── Audit modal ───────────────────────────────────────────────── */

interface AuditEntry {
  id: number;
  action: string;
  before: unknown;
  after: unknown;
  createdAt: string;
  adminUserId: string;
  adminName: string | null;
  adminEmail: string | null;
}

function ProjectAuditModal({
  project,
  onClose,
}: {
  project: AdminProject;
  onClose: () => void;
}) {
  const auditQ = useQuery({
    queryKey: ['admin', 'projects', 'audit', project.id],
    queryFn: () =>
      api.get<{ entries: AuditEntry[] }>(`/admin/projects/${project.id}/audit`),
  });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-xl rounded-2xl border border-charcoal/12 bg-bone shadow-[0_18px_40px_rgba(43,42,47,0.18)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="border-b border-charcoal/10 px-5 py-4">
          <h2 className="text-[12px] font-bold uppercase tracking-[0.18em] text-pine">
            Audit log - {project.name}
          </h2>
          <p className="mt-1 text-[12px] text-charcoal/65">
            Admin actions taken on this project (renames, archive
            toggles, alias edits, merges). Meeting edits appear on their
            own rows.
          </p>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-5 py-3 text-[13px]">
          {auditQ.isLoading && (
            <div className="py-4 text-center italic text-charcoal/55">
              Loading…
            </div>
          )}
          {auditQ.data && auditQ.data.entries.length === 0 && (
            <div className="py-4 text-center italic text-charcoal/55">
              No admin actions recorded for this project yet.
            </div>
          )}
          {auditQ.data && auditQ.data.entries.length > 0 && (
            <ul className="divide-y divide-charcoal/10">
              {auditQ.data.entries.map((e) => (
                <ProjectAuditRow key={e.id} entry={e} />
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-end border-t border-charcoal/10 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectAuditRow({ entry }: { entry: AuditEntry }) {
  const [showRaw, setShowRaw] = useState(false);
  const summary = summarizeProjectAudit(entry);
  const hasDiff = !!entry.before || !!entry.after;
  return (
    <li className="py-2.5">
      <div className="flex items-baseline justify-between gap-2 text-[13px]">
        <span className="font-medium text-charcoal">{summary.label}</span>
        <span className="text-[11px] text-charcoal/50">
          {new Date(entry.createdAt).toLocaleString()}
        </span>
      </div>
      {summary.detail && (
        <div className="mt-0.5 text-[12px] text-charcoal/70">
          {summary.detail}
        </div>
      )}
      <div className="mt-1 flex items-center justify-between gap-2">
        <div className="text-[11px] text-charcoal/55">
          by {entry.adminName ?? entry.adminEmail ?? '(unknown)'}
          <span className="ml-1.5 rounded-full bg-charcoal/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-charcoal/50">
            {entry.action}
          </span>
        </div>
        {hasDiff && (
          <button
            type="button"
            onClick={() => setShowRaw((v) => !v)}
            className="text-[11px] text-charcoal/45 hover:text-pine"
          >
            {showRaw ? 'Hide raw' : 'Show raw'}
          </button>
        )}
      </div>
      {showRaw && hasDiff && (
        <pre className="mt-2 overflow-x-auto rounded-md bg-paper-light p-2 text-[10px] text-charcoal/70">
          {JSON.stringify({ before: entry.before, after: entry.after }, null, 2)}
        </pre>
      )}
    </li>
  );
}

function summarizeProjectAudit(entry: AuditEntry): {
  label: string;
  detail: string | null;
} {
  if (entry.action === 'project.patch') {
    const before = (entry.before as Record<string, unknown>) ?? {};
    const after = (entry.after as Record<string, unknown>) ?? {};
    const changes: string[] = [];
    if (
      typeof before.name === 'string' &&
      typeof after.name === 'string' &&
      before.name !== after.name
    ) {
      changes.push(`renamed "${before.name}" → "${after.name}"`);
    }
    if (
      typeof before.archived === 'boolean' &&
      typeof after.archived === 'boolean' &&
      before.archived !== after.archived
    ) {
      changes.push(after.archived ? 'archived' : 'unarchived');
    }
    if (Array.isArray(before.aliases) && Array.isArray(after.aliases)) {
      const beforeSet = new Set(before.aliases as string[]);
      const afterSet = new Set(after.aliases as string[]);
      const added = [...afterSet].filter((a) => !beforeSet.has(a));
      const removed = [...beforeSet].filter((a) => !afterSet.has(a));
      if (added.length > 0) changes.push(`+alias ${added.join(', ')}`);
      if (removed.length > 0) changes.push(`−alias ${removed.join(', ')}`);
    }
    return {
      label: changes.length > 0 ? changes.join(' · ') : 'Edited',
      detail: null,
    };
  }
  if (entry.action === 'project.merge') {
    const before = (entry.before as { sourceName?: string } | null) ?? {};
    const after = (entry.after as { reparentedMeetings?: number } | null) ?? {};
    return {
      label: `Absorbed "${before.sourceName ?? 'another project'}"`,
      detail: after.reparentedMeetings
        ? `${after.reparentedMeetings} meeting${after.reparentedMeetings === 1 ? '' : 's'} reparented.`
        : null,
    };
  }
  return { label: entry.action, detail: null };
}
